import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from '../interfaces/api.interface';
import { Role, IRole } from '../models/role.model';
import { IModule, Module } from '../models/module.model';

export interface CreateRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  modulePermissions?: Array<{
    moduleId: number;
    permissions: string[];
  }>;
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  modulePermissions?: Array<{
    moduleId: number;
    permissions: string[];
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyRolesService {
  private baseUrl = '/api/company/roles';
  
  constructor(private http: HttpClient) {}
  
  /**
   * Get all roles for current company
   */
  getCompanyRoles(): Observable<ApiResponse<Role[]>> {
    return this.http.get<ApiResponse<IRole[]>>(this.baseUrl).pipe(
      map(response => ({
        ...response,
        data: response.data?.map((role: IRole) => new Role(role)) || []
      }))
    );
  }
  
  /**
   * Get available modules filtered by company type
   */
  getCompanyModules(): Observable<ApiResponse<Module[]>> {
    return this.http.get<ApiResponse<IModule[]>>(`${this.baseUrl}/modules`).pipe(
      map(response => ({
        ...response,
        data: response.data?.map((module: IModule) => new Module(module)) || []
      }))
    );
  }
  
  /**
   * Get role by ID
   */
  getRoleById(id: number): Observable<ApiResponse<Role>> {
    return this.http.get<ApiResponse<IRole>>(`${this.baseUrl}/${id}`).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : undefined
      }))
    );
  }
  
  /**
   * Create new company role (companyId auto-injected by backend)
   */
  createRole(roleData: CreateRoleDto): Observable<ApiResponse<Role>> {
    return this.http.post<ApiResponse<IRole>>(this.baseUrl, roleData).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : undefined
      }))
    );
  }
  
  /**
   * Update role
   */
  updateRole(id: number, roleData: UpdateRoleDto): Observable<ApiResponse<Role>> {
    return this.http.put<ApiResponse<IRole>>(`${this.baseUrl}/${id}`, roleData).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : undefined
      }))
    );
  }
  
  /**
   * Delete role
   */
  deleteRole(id: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }
}
